/**
 * AGENT MARKETPLACE - PREMIUM EDITION
 * 
 * The grand entrance to iSupplyAI's AI Workforce.
 * Design Philosophy: Premium SaaS meets sci-fi command center
 * 
 * Features:
 * - Animated hero section with particle effects
 * - Glass-morphism agent cards with glow effects
 * - Live capability previews
 * - Social proof and success metrics
 * - Smooth micro-interactions throughout
 */

import React, { useState, useEffect, useMemo } from 'react';

// Pre-generate particle positions (stable across renders)
const PARTICLE_POSITIONS = Array.from({ length: 20 }, (_, i) => ({
  left: (i * 5 + (i % 3) * 17) % 100,
  top: (i * 7 + (i % 5) * 13) % 100,
  delay: (i * 0.25) % 5,
  duration: 5 + (i % 10)
}));

// Animated background particles
const ParticleField = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {PARTICLE_POSITIONS.map((pos, i) => (
        <div
          key={i}
          className="absolute w-1 h-1 bg-purple-500/30 rounded-full animate-float"
          style={{
            left: `${pos.left}%`,
            top: `${pos.top}%`,
            animationDelay: `${pos.delay}s`,
            animationDuration: `${pos.duration}s`
          }}
        />
      ))}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); opacity: 0.3; }
          25% { transform: translateY(-20px) translateX(10px); opacity: 0.8; }
          50% { transform: translateY(-10px) translateX(-10px); opacity: 0.5; }
          75% { transform: translateY(-30px) translateX(5px); opacity: 0.7; }
        }
        .animate-float { animation: float 10s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

// Glowing orb effect for featured items
const GlowOrb = ({ color = 'purple', size = 'lg', className = '' }) => {
  const sizes = { sm: 'w-32 h-32', md: 'w-48 h-48', lg: 'w-64 h-64', xl: 'w-96 h-96' };
  const colors = {
    purple: 'from-purple-500/20 to-blue-500/20',
    amber: 'from-amber-500/20 to-orange-500/20',
    cyan: 'from-cyan-500/20 to-emerald-500/20'
  };
  
  return (
    <div className={`absolute ${sizes[size]} rounded-full bg-gradient-radial ${colors[color]} blur-3xl ${className}`} />
  );
};

// Premium stat badge with animation
const StatBadge = ({ icon, value, label, color = 'purple' }) => {
  const [displayed, setDisplayed] = useState(0);
  
  useEffect(() => {
    const target = parseInt(value) || 0;
    const duration = 1500;
    const steps = 30;
    const increment = target / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setDisplayed(target);
        clearInterval(timer);
      } else {
        setDisplayed(Math.floor(current));
      }
    }, duration / steps);
    
    return () => clearInterval(timer);
  }, [value]);
  
  const colors = {
    purple: 'from-purple-500 to-blue-500 shadow-purple-500/25',
    amber: 'from-amber-500 to-orange-500 shadow-amber-500/25',
    cyan: 'from-cyan-500 to-emerald-500 shadow-cyan-500/25',
    emerald: 'from-emerald-500 to-teal-500 shadow-emerald-500/25'
  };
  
  return (
    <div className="relative group">
      <div className={`absolute inset-0 bg-gradient-to-r ${colors[color]} rounded-2xl blur-xl opacity-0 group-hover:opacity-50 transition-opacity duration-500`} />
      <div className="relative bg-gray-900/80 backdrop-blur-xl rounded-2xl p-6 border border-gray-800 hover:border-gray-700 transition-all duration-300">
        <div className="text-3xl mb-2">{icon}</div>
        <div className={`text-3xl font-bold bg-gradient-to-r ${colors[color]} bg-clip-text text-transparent`}>
          {typeof value === 'number' ? displayed.toLocaleString() : value}
        </div>
        <div className="text-sm text-gray-400 mt-1">{label}</div>
      </div>
    </div>
  );
};

// Capability preview chip
const CapabilityChip = ({ icon, label, description, isHighlighted }) => (
  <div className={`
    group relative px-4 py-2 rounded-xl border transition-all duration-300 cursor-default
    ${isHighlighted 
      ? 'bg-gradient-to-r from-purple-500/20 to-blue-500/20 border-purple-500/50 text-purple-300' 
      : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:border-gray-600 hover:text-gray-300'}
  `}>
    <div className="flex items-center gap-2">
      <span>{icon}</span>
      <span className="font-medium text-sm">{label}</span>
    </div>
    {description && (
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 rounded-lg text-xs text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 border border-gray-700">
        {description}
        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
      </div>
    )}
  </div>
);

// Premium Agent Card
const PremiumAgentCard = ({ agent, canHire, onHire, onLearnMore, featured = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  
  const agentStyles = {
    content_engine: {
      gradient: 'from-purple-600 via-blue-600 to-indigo-600',
      glow: 'shadow-purple-500/30',
      accent: 'purple',
      bgGlow: 'from-purple-500/10 to-blue-500/10'
    },
    competitor_spy: {
      gradient: 'from-amber-500 via-orange-500 to-red-500',
      glow: 'shadow-amber-500/30',
      accent: 'amber',
      bgGlow: 'from-amber-500/10 to-orange-500/10'
    },
    distribution_machine: {
      gradient: 'from-emerald-500 via-cyan-500 to-teal-500',
      glow: 'shadow-emerald-500/30',
      accent: 'cyan',
      bgGlow: 'from-emerald-500/10 to-cyan-500/10'
    }
  };
  
  const style = agentStyles[agent.agent_type] || agentStyles.content_engine;
  
  return (
    <div 
      className={`
        relative group rounded-3xl overflow-hidden transition-all duration-500
        ${featured ? 'col-span-full lg:col-span-2' : ''}
        ${isHovered ? `shadow-2xl ${style.glow} scale-[1.02]` : 'shadow-xl'}
      `}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Animated border gradient */}
      <div className={`absolute inset-0 bg-gradient-to-r ${style.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
      <div className="absolute inset-[1px] bg-gray-900 rounded-3xl" />
      
      {/* Background glow effect */}
      <div className={`absolute inset-0 bg-gradient-to-br ${style.bgGlow} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
      
      {/* Content */}
      <div className="relative p-8">
        {/* Featured badge */}
        {featured && (
          <div className="absolute top-4 right-4">
            <div className={`px-4 py-1.5 rounded-full bg-gradient-to-r ${style.gradient} text-white text-sm font-bold flex items-center gap-2`}>
              <span className="animate-pulse">⭐</span> FEATURED
            </div>
          </div>
        )}
        
        {/* Header */}
        <div className={`flex ${featured ? 'flex-row items-start gap-8' : 'flex-col'}`}>
          <div className={`flex items-start gap-5 ${featured ? 'flex-1' : 'mb-6'}`}>
            {/* Agent Avatar */}
            <div className="relative">
              <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${style.gradient} flex items-center justify-center text-4xl shadow-lg ${style.glow} transition-transform duration-300 group-hover:scale-110`}>
                {agent.emoji}
              </div>
              {/* Online indicator */}
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full border-2 border-gray-900 flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
              </div>
            </div>
            
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-white mb-1 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:bg-clip-text group-hover:from-white group-hover:to-gray-300 transition-all duration-300">
                {agent.display_name}
              </h3>
              <p className={`text-transparent bg-gradient-to-r ${style.gradient} bg-clip-text font-semibold mb-2`}>
                {agent.tagline}
              </p>
              
              {/* Ratings & Users */}
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <span className="text-amber-400">★</span>
                  <span className="text-white font-semibold">{agent.rating}</span>
                  <span className="text-gray-500">({agent.total_users?.toLocaleString()} users)</span>
                </div>
                <div className="h-4 w-px bg-gray-700" />
                <div className="flex items-center gap-1 text-emerald-400">
                  <span>✓</span>
                  <span>{agent.success_rate || '99'}% success rate</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Quick Stats for Featured */}
          {featured && (
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center px-4 py-3 bg-gray-800/50 rounded-xl">
                <div className="text-2xl font-bold text-purple-400">~{agent.credits_per_run}</div>
                <div className="text-xs text-gray-500">credits/run</div>
              </div>
              <div className="text-center px-4 py-3 bg-gray-800/50 rounded-xl">
                <div className="text-2xl font-bold text-emerald-400">{agent.time_saved_hours?.split(' ')[0] || '8-10'}</div>
                <div className="text-xs text-gray-500">hours saved</div>
              </div>
              <div className="text-center px-4 py-3 bg-gray-800/50 rounded-xl">
                <div className="text-2xl font-bold text-cyan-400">24/7</div>
                <div className="text-xs text-gray-500">availability</div>
              </div>
            </div>
          )}
        </div>
        
        {/* Description */}
        <p className="text-gray-400 leading-relaxed my-6">
          {agent.description}
        </p>
        
        {/* Capabilities */}
        <div className="mb-6">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Capabilities</p>
          <div className="flex flex-wrap gap-2">
            {agent.features?.slice(0, featured ? 8 : 5).map((feature, idx) => (
              <CapabilityChip 
                key={idx}
                icon="✓"
                label={feature}
                isHighlighted={idx < 3}
              />
            ))}
            {agent.features?.length > (featured ? 8 : 5) && (
              <div className="px-3 py-2 text-sm text-gray-500">
                +{agent.features.length - (featured ? 8 : 5)} more
              </div>
            )}
          </div>
        </div>
        
        {/* Sample Output Preview Toggle */}
        <button
          onClick={() => setShowPreview(!showPreview)}
          className="w-full mb-6 px-4 py-3 rounded-xl border border-gray-700 text-gray-400 hover:border-gray-600 hover:text-gray-300 transition-all duration-300 flex items-center justify-center gap-2"
        >
          <span>{showPreview ? '🔼' : '👁️'}</span>
          <span>{showPreview ? 'Hide Preview' : 'See What You Get'}</span>
        </button>
        
        {/* Preview Panel */}
        {showPreview && (
          <div className="mb-6 p-4 bg-gray-800/50 rounded-xl border border-gray-700 animate-fadeIn">
            <p className="text-xs text-gray-500 mb-3">SAMPLE OUTPUT</p>
            {agent.agent_type === 'content_engine' && (
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2"><span>📝</span><span className="text-gray-300">1,500 word SEO-optimized blog post</span></div>
                <div className="flex items-center gap-2"><span>📱</span><span className="text-gray-300">5 platform-specific social posts</span></div>
                <div className="flex items-center gap-2"><span>📧</span><span className="text-gray-300">Email newsletter version</span></div>
                <div className="flex items-center gap-2"><span>🎨</span><span className="text-gray-300">AI-generated featured image</span></div>
                <div className="flex items-center gap-2"><span>#️⃣</span><span className="text-gray-300">Hashtag strategy per platform</span></div>
              </div>
            )}
            {agent.agent_type === 'competitor_spy' && (
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2"><span>🎯</span><span className="text-gray-300">Competitor identification & analysis</span></div>
                <div className="flex items-center gap-2"><span>🔍</span><span className="text-gray-300">Content gap opportunities</span></div>
                <div className="flex items-center gap-2"><span>⚔️</span><span className="text-gray-300">Strategic battle plan</span></div>
                <div className="flex items-center gap-2"><span>💡</span><span className="text-gray-300">Topic recommendations</span></div>
              </div>
            )}
          </div>
        )}
        
        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onLearnMore}
            className="flex-1 px-6 py-4 rounded-xl border border-gray-700 text-gray-300 hover:border-gray-500 hover:text-white transition-all duration-300 font-medium"
          >
            Learn More
          </button>
          <button
            onClick={onHire}
            disabled={!canHire}
            className={`
              flex-1 px-6 py-4 rounded-xl font-bold transition-all duration-300 relative overflow-hidden
              ${canHire
                ? `bg-gradient-to-r ${style.gradient} text-white hover:shadow-lg ${style.glow} hover:scale-[1.02]`
                : 'bg-gray-800 text-gray-500 cursor-not-allowed'}
            `}
          >
            {canHire ? (
              <>
                <span className="relative z-10">{agent.emoji} Hire {agent.display_name}</span>
                <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 skew-x-12" />
              </>
            ) : (
              'Upgrade to Hire'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Marketplace Component
const AgentMarketplace = ({ API, user, onHireAgent, onViewAgent, onProFeaturePreview }) => {
  const [marketplace, setMarketplace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchMarketplace();
  }, []);

  const fetchMarketplace = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/agents/marketplace`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to load marketplace');
      
      const data = await response.json();
      setMarketplace(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[600px] flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-24 h-24 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full border-4 border-purple-500/20" />
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-purple-500 animate-spin" />
            <div className="absolute inset-4 rounded-full bg-gradient-to-br from-purple-500/20 to-blue-500/20 animate-pulse" />
            <div className="absolute inset-0 flex items-center justify-center text-3xl">🤖</div>
          </div>
          <p className="text-gray-400 text-lg">Initializing AI Workforce...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-8 text-center">
        <div className="text-4xl mb-4">⚠️</div>
        <p className="text-red-400 mb-4">{error}</p>
        <button 
          onClick={fetchMarketplace}
          className="px-6 py-3 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-colors font-medium"
        >
          Try Again
        </button>
      </div>
    );
  }

  const availableAgents = marketplace?.available_agents?.filter(a => a.is_available) || [];
  const comingSoonAgents = marketplace?.available_agents?.filter(a => a.coming_soon) || [];
  const featuredAgent = availableAgents[0]; // First agent is featured
  const otherAgents = availableAgents.slice(1);

  return (
    <div className="relative min-h-screen bg-[#0a0a0f] text-white overflow-hidden">
      {/* Background Effects */}
      <ParticleField />
      <GlowOrb color="purple" size="xl" className="-top-32 -left-32 opacity-30" />
      <GlowOrb color="cyan" size="lg" className="top-1/2 -right-24 opacity-20" />
      <GlowOrb color="amber" size="md" className="bottom-0 left-1/3 opacity-20" />
      
      {/* Hero Section */}
      <div className="relative z-10 text-center pt-12 pb-16 px-6">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20 mb-6">
          <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
          <span className="text-sm text-gray-300">{marketplace?.total_agents_hired?.toLocaleString() || '10,000'}+ agents deployed worldwide</span>
        </div>
        
        <h1 className="text-5xl md:text-6xl font-bold mb-6">
          <span className="bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
            Your AI Workforce
          </span>
          <br />
          <span className="bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
            Awaits
          </span>
        </h1>
        
        <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-12 leading-relaxed">
          Hire intelligent agents that work 24/7, creating content, analyzing competitors, 
          and growing your brand while you focus on what matters.
        </p>
        
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-12">
          <StatBadge icon="🤖" value={marketplace?.available_agents?.length || 3} label="AI Agents" color="purple" />
          <StatBadge icon="⚡" value={marketplace?.total_runs_completed || 50000} label="Tasks Completed" color="cyan" />
          <StatBadge icon="⏱️" value="500K+" label="Hours Saved" color="emerald" />
          <StatBadge icon="💰" value="$2M+" label="Value Generated" color="amber" />
        </div>
      </div>
      
      {/* Capacity Banner */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 mb-12">
        <div className="relative overflow-hidden rounded-2xl">
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 via-blue-600/20 to-cyan-600/20" />
          <div className="relative backdrop-blur-xl border border-white/10 rounded-2xl p-6">
            <div className="flex flex-wrap items-center justify-between gap-6">
              <div className="flex items-center gap-6">
                <div className="relative">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                    <span className="text-2xl">👤</span>
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-gray-900 rounded-full flex items-center justify-center text-xs font-bold text-purple-400 border-2 border-purple-500">
                    {marketplace?.user_active_agents || 0}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-400">Your Active Agents</p>
                  <p className="text-3xl font-bold">
                    <span className="text-white">{marketplace?.user_active_agents || 0}</span>
                    <span className="text-gray-500 text-xl"> / {marketplace?.max_agents_for_tier || 3}</span>
                  </p>
                </div>
              </div>
              
              {marketplace?.can_hire_more ? (
                <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                  <div className="w-3 h-3 bg-emerald-400 rounded-full animate-pulse" />
                  <span className="text-emerald-400 font-semibold">Ready to hire more agents</span>
                </div>
              ) : (
                <button className="px-6 py-3 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold hover:shadow-lg hover:shadow-purple-500/25 transition-all duration-300">
                  Upgrade for More Agents →
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Free User CTA */}
      {user?.subscription_tier === 'free' && (
        <div className="relative z-10 max-w-6xl mx-auto px-6 mb-12">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/20 p-8">
            <div className="flex items-center gap-6">
              <div className="text-5xl">⚡</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-2">Unlock Your AI Workforce</h3>
                <p className="text-gray-400">
                  AI Agents are available on Pro and Enterprise plans. Upgrade now to hire Hermes, Athena, and more!
                </p>
              </div>
              <button className="px-8 py-4 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold hover:shadow-lg hover:shadow-amber-500/25 transition-all duration-300">
                Upgrade to Pro →
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Featured Agent */}
      {featuredAgent && (
        <div className="relative z-10 max-w-6xl mx-auto px-6 mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
            <h2 className="text-2xl font-bold text-white">Featured Agent</h2>
          </div>
          <PremiumAgentCard
            agent={featuredAgent}
            canHire={marketplace?.can_hire_more && user?.subscription_tier !== 'free'}
            onHire={() => onHireAgent(featuredAgent)}
            onLearnMore={() => onViewAgent(featuredAgent)}
            featured={true}
          />
        </div>
      )}
      
      {/* Other Agents */}
      {otherAgents.length > 0 && (
        <div className="relative z-10 max-w-6xl mx-auto px-6 mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-2 h-2 bg-emerald-500 rounded-full" />
            <h2 className="text-2xl font-bold text-white">More Agents</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {otherAgents.map(agent => (
              <PremiumAgentCard
                key={agent.agent_type}
                agent={agent}
                canHire={marketplace?.can_hire_more && user?.subscription_tier !== 'free'}
                onHire={() => onHireAgent(agent)}
                onLearnMore={() => onViewAgent(agent)}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Pro Features Preview - Show to free users */}
      {user?.subscription_tier === 'free' && (
        <div className="relative z-10 max-w-6xl mx-auto px-6 pb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse" />
            <h2 className="text-2xl font-bold text-white">Pro Features</h2>
            <span className="px-3 py-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold rounded-full">
              UPGRADE TO UNLOCK
            </span>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Athena Preview */}
            <button
              onClick={() => onProFeaturePreview && onProFeaturePreview('athena')}
              className="group relative p-5 rounded-2xl bg-gradient-to-br from-amber-900/20 to-orange-900/20 border border-amber-500/30 hover:border-amber-500/60 transition-all duration-300 text-left overflow-hidden"
              data-testid="athena-preview-card"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center text-xl">🦉</div>
                  <div>
                    <h3 className="font-semibold text-amber-300">Athena's War Room</h3>
                    <p className="text-xs text-gray-400">Competitive Intelligence</p>
                  </div>
                </div>
                <p className="text-sm text-gray-400 mb-3">Spy on competitors, find gaps, dominate markets</p>
                <div className="flex items-center text-amber-400 text-sm font-medium group-hover:text-amber-300">
                  <span>Preview</span>
                  <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </div>
              </div>
            </button>

            {/* Hermes Preview */}
            <button
              onClick={() => onProFeaturePreview && onProFeaturePreview('hermes-forge')}
              className="group relative p-5 rounded-2xl bg-gradient-to-br from-orange-900/20 to-red-900/20 border border-orange-500/30 hover:border-orange-500/60 transition-all duration-300 text-left overflow-hidden"
              data-testid="hermes-preview-card"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center text-xl">👟</div>
                  <div>
                    <h3 className="font-semibold text-orange-300">Hermes' Forge</h3>
                    <p className="text-xs text-gray-400">Content Creation</p>
                  </div>
                </div>
                <p className="text-sm text-gray-400 mb-3">Create viral content at scale with AI</p>
                <div className="flex items-center text-orange-400 text-sm font-medium group-hover:text-orange-300">
                  <span>Preview</span>
                  <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </div>
              </div>
            </button>

            {/* Apollo Preview */}
            <button
              onClick={() => onProFeaturePreview && onProFeaturePreview('apollo-chamber')}
              className="group relative p-5 rounded-2xl bg-gradient-to-br from-yellow-900/20 to-orange-900/20 border border-yellow-500/30 hover:border-yellow-500/60 transition-all duration-300 text-left overflow-hidden"
              data-testid="apollo-preview-card"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/5 to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center text-xl">☀️</div>
                  <div>
                    <h3 className="font-semibold text-yellow-300">Apollo's Chamber</h3>
                    <p className="text-xs text-gray-400">Multi-Platform Distribution</p>
                  </div>
                </div>
                <p className="text-sm text-gray-400 mb-3">Distribute to 5+ platforms in one click</p>
                <div className="flex items-center text-yellow-400 text-sm font-medium group-hover:text-yellow-300">
                  <span>Preview</span>
                  <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </div>
              </div>
            </button>

            {/* Dionysus Preview */}
            <button
              onClick={() => onProFeaturePreview && onProFeaturePreview('dionysus')}
              className="group relative p-5 rounded-2xl bg-gradient-to-br from-purple-900/20 to-red-900/20 border border-purple-500/30 hover:border-purple-500/60 transition-all duration-300 text-left overflow-hidden"
              data-testid="dionysus-preview-card"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-red-500/5 opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center text-xl">🍷</div>
                  <div>
                    <h3 className="font-semibold text-purple-300">Dionysus</h3>
                    <p className="text-xs text-gray-400">Reddit Growth Engine</p>
                  </div>
                </div>
                <p className="text-sm text-gray-400 mb-3">Find threads, craft replies, grow organically</p>
                <div className="flex items-center text-purple-400 text-sm font-medium group-hover:text-purple-300">
                  <span>Preview</span>
                  <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                </div>
              </div>
            </button>
          </div>
        </div>
      )}
      
      {/* Coming Soon */}
      {comingSoonAgents.length > 0 && (
        <div className="relative z-10 max-w-6xl mx-auto px-6 pb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-2 h-2 bg-amber-500 rounded-full" />
            <h2 className="text-2xl font-bold text-white">Coming Soon</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {comingSoonAgents.map(agent => (
              <div 
                key={agent.agent_type}
                className="relative group p-6 rounded-2xl bg-gray-900/50 border border-gray-800 opacity-60 hover:opacity-80 transition-opacity"
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 rounded-xl bg-gray-800 flex items-center justify-center text-2xl">
                    {agent.emoji}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-400">{agent.display_name}</h3>
                    <p className="text-sm text-gray-600">{agent.tagline}</p>
                  </div>
                </div>
                <span className="inline-block px-3 py-1 bg-gray-800 text-gray-500 rounded-full text-sm">
                  Coming Soon
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Animation styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default AgentMarketplace;
